import {useEffect, useLayoutEffect, useRef, type RefObject} from 'react'

/** The attributes `useTableScrollEdges` keeps on the scroll container ("true" / "false"). */
export const TABLE_SCROLL_EDGE_ATTRIBUTES = {
  start: 'data-at-start',
  end: 'data-at-end',
} as const

/** Sub-pixel scroll positions (a zoomed page) still count as "at the edge" within this. */
const EDGE_TOLERANCE = 1

const setFlag = (element: HTMLElement, name: string, value: boolean) => {
  const text = String(value)
  // Only a change is written, so a scroll that stays mid-table restyles nothing.
  if (element.getAttribute(name) !== text) element.setAttribute(name, text)
}

/** `:focus-visible`, or true where the selector is unsupported (better to reveal than to hide). */
const isKeyboardFocus = (element: Element) => {
  try {
    return element.matches(':focus-visible')
  } catch {
    return true
  }
}

/**
 * WCAG 2.2 SC 2.4.11 (Focus Not Obscured): a keyboard-focused control in a scrolling cell can sit
 * inside the scrollport yet entirely under its row's sticky cells, and browsers do not scroll it out.
 * This scrolls the container just enough to clear them, with the cell's inline padding to spare
 * for the focus ring. A control IN a sticky cell is never covered and is left alone; a mouse focus
 * does not scroll. Not `scroll-padding`: that makes the browser's own focus scroll jump the table
 * when focus lands on a sticky cell's own control.
 */
const revealFocusedControl = (container: HTMLElement) => (event: FocusEvent) => {
  const target = event.target
  if (!(target instanceof Element) || !isKeyboardFocus(target)) return
  const cell = target.closest('th, td')
  if (!cell?.parentElement || !container.contains(cell)) return
  if (getComputedStyle(cell).position === 'sticky') return
  const rtl = getComputedStyle(container).direction === 'rtl'
  const box = container.getBoundingClientRect()
  let left = box.left + container.clientLeft
  let right = left + container.clientWidth
  let beforeCell = true
  for (const sibling of Array.from(cell.parentElement.children)) {
    if (sibling === cell) {
      beforeCell = false
      continue
    }
    if (getComputedStyle(sibling).position !== 'sticky') continue
    const cover = sibling.getBoundingClientRect()
    // A sticky cell before this one sits at the inline start: the left (the right in RTL).
    if (beforeCell !== rtl) left = Math.max(left, cover.right)
    else right = Math.min(right, cover.left)
  }
  const focused = target.getBoundingClientRect()
  // Clear the focus indicator as well as the control: the DS --focus-ring (a 4px box-shadow) and a
  // browser's default outline paint outside the border box. The cell's inline padding is the room
  // the table gives that indicator, so the reveal keeps that much clear of the sticky cells too.
  const padding = getComputedStyle(cell)
  const start = focused.left - (parseFloat(padding.paddingLeft) || 0)
  const end = focused.right + (parseFloat(padding.paddingRight) || 0)
  if (start < left) container.scrollLeft -= left - start
  else if (end > right) container.scrollLeft += Math.min(end - right, start - left)
}

/** Keeps the two attributes on `container` current; returns the teardown. */
function watchScrollEdges(container: HTMLElement): () => void {
  const update = () => {
    const overflow = container.scrollWidth - container.clientWidth
    // A right-to-left container scrolls to negative offsets.
    const offset = Math.abs(container.scrollLeft)
    setFlag(container, TABLE_SCROLL_EDGE_ATTRIBUTES.start, offset <= EDGE_TOLERANCE)
    setFlag(container, TABLE_SCROLL_EDGE_ATTRIBUTES.end, offset >= overflow - EDGE_TOLERANCE)
  }

  // The container's own size, and the size of what it holds (a table that gains or loses a column,
  // or is swapped for an empty state), both move the edges without a scroll.
  const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
  const observeChildren = () =>
    Array.from(container.children).forEach((child) => resizeObserver?.observe(child))
  resizeObserver?.observe(container)
  observeChildren()
  const mutationObserver =
    typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver((records) => {
          // A child swapped out (the skeleton, the empty state) is no longer watched.
          for (const record of records) {
            record.removedNodes.forEach((node) => {
              if (node instanceof Element && node.parentNode !== container)
                resizeObserver?.unobserve(node)
            })
          }
          observeChildren()
          update()
        })
  mutationObserver?.observe(container, {childList: true})
  container.addEventListener('scroll', update, {passive: true})
  const onFocusIn = revealFocusedControl(container)
  container.addEventListener('focusin', onFocusIn)
  update()

  return () => {
    container.removeEventListener('scroll', update)
    container.removeEventListener('focusin', onFocusIn)
    resizeObserver?.disconnect()
    mutationObserver?.disconnect()
    container.removeAttribute(TABLE_SCROLL_EDGE_ATTRIBUTES.start)
    container.removeAttribute(TABLE_SCROLL_EDGE_ATTRIBUTES.end)
  }
}

/**
 * Marks a horizontally scrolling table container with `data-at-start` / `data-at-end` ("true" /
 * "false"), kept current from its scroll events, a `ResizeObserver` on it and on what it holds, and
 * a `MutationObserver` for a child swapped in (STFFLG-4179). The DS Table's sticky cells read them:
 * a `sticky="start"` cell shows `--shadow-sticky-edge` while content is scrolled under it (not at
 * the start), a `sticky="end"` cell while there is content left to scroll to (not at the end).
 *
 * It also keeps keyboard focus clear of the sticky cells (WCAG 2.2 SC 2.4.11): when a control in a
 * scrolling cell takes keyboard focus (`:focus-visible`) under its row's sticky cells, it scrolls
 * the container just enough to show it and its focus ring. A control in a sticky cell, and a mouse
 * focus, never scroll.
 *
 * `ref` is the scroll container (the element with `overflow-x: auto`). The hook follows the
 * element `ref` points at after each render, so a container that unmounts and mounts again (a tab
 * switch) is picked up. `enabled: false` sets nothing, listens to nothing, and removes what it set.
 */
export function useTableScrollEdges(ref: RefObject<HTMLElement>, enabled = true): void {
  const watched = useRef<{element: HTMLElement; stop: () => void} | null>(null)

  // After every commit: start watching the element the ref now holds, if it is a new one.
  useLayoutEffect(() => {
    const element = enabled ? ref.current : null
    if (watched.current?.element === element) return
    watched.current?.stop()
    watched.current = element ? {element, stop: watchScrollEdges(element)} : null
  })

  useEffect(
    () => () => {
      watched.current?.stop()
      watched.current = null
    },
    [],
  )
}
